import React, { useRef, useState } from "react";
import "./NewPassword.css";
import GreenCheck from "../../../assets/images/auth/checkbox/green-check.svg";
import RedCheck from "../../../assets/images/auth/checkbox/red-check.svg";
import GreyCheck from "../../../assets/images/auth/checkbox/gray-check.svg";
import Logoo from "../../../assets/images/auth/Logo.svg";
import { useNavigate } from "react-router-dom";
import axios from "../../../util/axiosconfig";
import { showError, showSuccess } from "../../../util/ShowStatus";
import { Toast } from "primereact/toast";
import { InputText } from "primereact/inputtext";
import { FloatLabel } from "primereact/floatlabel";
import { Button } from "primereact/button";

interface NewPasswordProps {
  token: string | null;
}

const NewPassword: React.FC<NewPasswordProps> = ({ token }) => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [message, setMessage] = useState("Must be at least 8 characters.");
  const [messageColor, setMessageColor] = useState("");
  const [buttonImage, setButtonImage] = useState(GreyCheck);
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [specialCharMessageColor, setSpecialCharMessageColor] =
    useState("grey");
  const [specialCharacterMessageButton, setSpecialCharacterMessageButton] =
    useState(GreyCheck);
  const specialCharMessage = "Must contain at least one special character.";
  const specialCharRegex = /[@#$%^&*()_+\-={}[\]|:;"'<>,.?/~€¥£]/;
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const toast = useRef<Toast>(null);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      setMessage("Passwords do not match.");
      setMessageColor("red");
      setButtonImage(RedCheck);
      return;
    } else {
      setMessageColor("green");
      setButtonImage(GreenCheck);
    }

    try {
      const payload = {
        token,
        newPassword: password,
      };
      setButtonDisabled(true);
      const response = await axios.post("auth/set-password", payload);
      showSuccess(response.data.Message, toast);
      setTimeout(() => {
        navigate("/");
        setButtonDisabled(false);
      }, 2000);
    } catch (error: any) {
      console.error("Error resetting password:", error);
      showError(error.response.data.Message, toast);
      setButtonDisabled(false);
    }
  };

  const validatePasswords = (
    newPassword: string,
    newConfirmPassword: string
  ) => {
    if (newPassword && newConfirmPassword) {
      if (newPassword === newConfirmPassword) {
        setMessage("Passwords match.");
        setMessageColor("green");
        setButtonImage(GreenCheck);
      } else {
        setMessage("Passwords do not match.");
        setMessageColor("red");
        setButtonImage(RedCheck);
      }
    }
  };

  const handlePasswordInputChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const input = e.target.value;
    const minLength = 8;
    const maxLength = 16;

    setPassword(input);
    validatePasswords(input, confirmPassword);

    if (input.length === 0) {
      setMessage(`Must be between ${minLength} to ${maxLength} characters.`);
      setMessageColor("grey");
      setButtonImage(GreyCheck);
      setSpecialCharMessageColor("grey");
      setSpecialCharacterMessageButton(GreyCheck);
    } else if (input.length < minLength) {
      setMessage(`Must be at least ${minLength} characters.`);
      setMessageColor("red");
      setButtonImage(RedCheck);
      setSpecialCharMessageColor("grey");
      setSpecialCharacterMessageButton(GreyCheck);
    } else if (input.length > maxLength) {
      setMessage(`Must be no more than ${maxLength} characters.`);
      setMessageColor("red");
      setButtonImage(RedCheck);
      setSpecialCharMessageColor("grey");
      setSpecialCharacterMessageButton(GreyCheck);
    } else {
      setMessage(`Length: ${input.length} characters.`);
      setMessageColor("#28852b");
      setButtonImage(GreenCheck);

      if (!specialCharRegex.test(input)) {
        setSpecialCharMessageColor("red");
        setSpecialCharacterMessageButton(RedCheck);
      } else {
        setSpecialCharMessageColor("#28852b");
        setSpecialCharacterMessageButton(GreenCheck);
      }
    }
  };

  return (
    <>
      <Toast ref={toast} />
      <div className="tw-flex tw-flex-col md:tw-flex-row tw-h-screen login-container">
        {/* Mobile Logo */}
        <div className="tw-w-full tw-mt-12 tw-ml-[-10px] tw-flex tw-items-center tw-justify-center tw-mb-4 md:tw-hidden">
          <img src={Logoo} alt="amelioLogoMobile" className="tw-w-72 tw-h-auto" />
        </div>

        {/* Left Section for Desktop */}
        <div className="tw-hidden md:tw-flex tw-items-center tw-justify-center tw-w-1/2 login-left">
          <img
            src={Logoo}
            alt="amelioLogoLargeScreen"
            className="tw-w-64 lg:tw-w-[490px] tw-h-auto"
          />
        </div>
        <div className="tw-w-full md:tw-w-1/2 tw-flex tw-items-center tw-justify-center tw-p-6">
          <div className="tw-w-full tw-max-w-lg">
            <h2 className="tw-text-2xl tw-font-bold tw-mb-4 tw-text-center">
              Set new password.
            </h2>
            <p className="tw-mb-6 tw-text-sm tw-text-center tw-items-center md:tw-text-left">
              Your new password must be different from previously used
              passwords.
            </p>
            <form onSubmit={handleSubmit} className="tw-space-y-6">
              <div className="tw-mt-4 tw-relative">
                <FloatLabel>
                  <InputText
                    type={showPassword ? "text" : "password"}
                    className="tw-w-full tw-px-2 tw-border tw-border-solid tw-border-gray-300 tw-h-[44px]"
                    value={password}
                    id="password"
                    onChange={(e) => {
                      setPassword(e.target.value);
                      handlePasswordInputChange(e);
                    }}
                    maxLength={16}
                    minLength={8}
                    required
                  />
                  <label htmlFor="password">Password</label>
                </FloatLabel>
                <span
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: "absolute",
                    right: "10px",
                    top: "12px",
                    cursor: "pointer",
                    color: "#999",
                  }}
                >
                  <i
                    className={showPassword ? "pi pi-eye" : "pi pi-eye-slash"}
                  ></i>
                </span>
              </div>
              <div className="tw-mt-4 tw-relative">
                <FloatLabel>
                  <InputText
                    type={showConfirmPassword ? "text" : "password"}
                    className="tw-w-full tw-px-2 tw-border tw-border-solid tw-border-gray-300 tw-h-[44px]"
                    value={confirmPassword}
                    id="confirmPassword"
                    onChange={(e) => {
                      const input = e.target.value;
                      setConfirmPassword(input);
                      validatePasswords(password, input);
                    }}
                    maxLength={16}
                    minLength={8}
                    required
                  />
                  <label htmlFor="confirmPassword">Confirm Password</label>
                </FloatLabel>
                {/* eye button design */}
                <span
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  style={{
                    position: "absolute",
                    right: "10px",
                    top: "12px",
                    cursor: "pointer",
                    color: "#999",
                  }}
                >
                  <i
                    className={
                      showConfirmPassword ? "pi pi-eye" : "pi pi-eye-slash"
                    }
                  ></i>
                </span>
              </div>
              <div className="tw-flex tw-items-center tw-mt-2">
                <img
                  src={buttonImage}
                  alt="Status"
                  className="tw-mr-2 tw-w-5 tw-h-5"
                />
                <p style={{ color: messageColor }}>{message}</p>
              </div>
              <div className="tw-flex tw-items-center">
                <img
                  src={specialCharacterMessageButton}
                  alt="Status"
                  className="tw-mr-2 tw-w-5 tw-h-5"
                />
                <p style={{ color: specialCharMessageColor }}>
                  {specialCharMessage}
                </p>
              </div>
              <button
                type="submit"
                className="tw-w-full tw-bg-blue-500 tw-text-white tw-py-2 tw-rounded tw-disabled:bg-gray-400"
                disabled={buttonDisabled}
              >
                {buttonDisabled ? "Redirecting..." : "Reset Password"}
              </button>
            </form>
            <div className="tw-text-center tw-mt-4 tw-font-semibold tw-leading-5 tw-text-[#555555]">
              <Button
                label="Back to Login"
                icon="pi pi-angle-left"
                onClick={() => navigate("/")}
                link
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default NewPassword;
